import React from "react";
import { MapPin, Phone, Mail, Clock, Star, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import Reveal from "@/components/Reveal";
import { GOOGLE_BUSINESS_URL, GOOGLE_REVIEW_URL, WHATSAPP_NUMBER_E164 } from "@/components/SocialLinks";

const MAP_EMBED_SRC =
  "https://www.google.com/maps?q=Calmbreeze+HVAC+14+Hyde+Lodge+West+Drayton+UB7+9FF&hl=en-GB&z=14&output=embed";

const HOURS = [
  { day: "Monday – Friday", hours: "07:30 – 18:00" },
  { day: "Saturday – Sunday", hours: "Emergency call-outs · 24/7" },
];

const GoogleBusinessSection = () => {
  return (
    <section
      className="py-24 md:py-32 bg-paper-warm border-y border-black/5"
      data-testid="google-business-section"
    >
      <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <Reveal>
          <div className="mb-12 md:mb-16">
            <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-3">
              [ Visit us on Google ]
            </div>
            <h2 className="font-display font-bold text-[2rem] md:text-[2.75rem] tracking-tightest leading-[1.05]">
              Calmbreeze on Google.
            </h2>
            <p className="text-[15px] md:text-[16px] text-muted-foreground max-w-2xl mt-4 leading-relaxed">
              Find our address, hours and directions — and if we&rsquo;ve looked after your site,
              a quick review helps a small London team like ours enormously.
            </p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Map — 7 cols on desktop */}
          <Reveal className="lg:col-span-7">
            <div className="relative w-full aspect-[4/3] md:aspect-[16/10] rounded-md overflow-hidden ring-1 ring-black/8 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.06)]">
              <iframe
                title="Calmbreeze on Google Maps"
                src={MAP_EMBED_SRC}
                className="absolute inset-0 w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="google-map-iframe"
              />
            </div>
          </Reveal>

          {/* Info card — 5 cols on desktop */}
          <Reveal className="lg:col-span-5">
            <div className="h-full flex flex-col bg-white border border-black/8 rounded-md p-8 md:p-9 shadow-[0_20px_60px_rgba(0,0,0,0.06)]">
              {/* Header — logo + name */}
              <div className="flex items-center gap-4 pb-5 border-b border-black/8">
                <Logo withWord={false} size="md" />
                <div>
                  <div className="font-display font-bold text-[1.15rem] leading-tight tracking-tight">
                    Calmbreeze
                  </div>
                  <div className="font-mono text-[10px] tracking-overline uppercase text-muted mt-1">
                    Commercial HVAC · West London
                  </div>
                </div>
              </div>

              {/* Rating */}
              <a
                href={GOOGLE_BUSINESS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-rating-link"
                className="mt-5 flex items-center gap-3 group"
              >
                <span className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-[#FBBC05]" fill="#FBBC05" strokeWidth={0} />
                  ))}
                </span>
                <span className="text-[13px] text-ink font-semibold group-hover:underline">
                  Rated on Google
                </span>
                <ArrowUpRight className="h-4 w-4 text-ink/40 group-hover:text-ink transition" />
              </a>

              {/* Details */}
              <ul className="mt-6 space-y-4 text-[13px] text-ink/80">
                <li className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-0.5 text-accent shrink-0" strokeWidth={2} />
                  <span>
                    14 Hyde Lodge, 11 Pennyroyal Drive,<br />West Drayton, UB7 9FF
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="h-4 w-4 mt-0.5 text-accent shrink-0" strokeWidth={2} />
                  <a href="tel:+441895919333" className="hover:text-ink hover:underline">
                    01895 919 333
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="h-4 w-4 mt-0.5 text-accent shrink-0" strokeWidth={2} />
                  <a href="mailto:Hello@calmbreeze.co.uk" className="hover:text-ink hover:underline">
                    Hello@calmbreeze.co.uk
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="h-4 w-4 mt-0.5 text-accent shrink-0" strokeWidth={2} />
                  <div className="flex-1">
                    {HOURS.map((h) => (
                      <div key={h.day} className="flex items-baseline justify-between gap-3">
                        <span className="text-ink/60">{h.day}</span>
                        <span className="text-ink font-medium tabular-nums">{h.hours}</span>
                      </div>
                    ))}
                  </div>
                </li>
              </ul>

              {/* CTAs */}
              <div className="mt-auto pt-6 flex flex-col sm:flex-row gap-3">
                <a
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="gbp-leave-review"
                  className="inline-flex items-center justify-center gap-2 flex-1 px-4 py-2.5 rounded-full bg-[#4285F4] hover:bg-[#3367d6] text-white text-[12px] font-semibold transition"
                >
                  <Star className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
                  Leave a review
                </a>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER_E164}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="gbp-whatsapp"
                  className="inline-flex items-center justify-center gap-2 flex-1 px-4 py-2.5 rounded-full border border-ink hover:bg-ink hover:text-white text-ink text-[12px] font-semibold transition"
                >
                  Message on WhatsApp
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default GoogleBusinessSection;
